import React, { useState } from 'react';
import { X, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { useConnectAdCredentials } from '../../hooks/useConnectAdCredentials';

interface Props {
  companyId: string | undefined;
  open: boolean;
  onClose: () => void;
  onConnected?: () => void;
}

export const ConnectMetaModal: React.FC<Props> = ({
  companyId,
  open,
  onClose,
  onConnected,
}) => {
  const { pasteMeta } = useConnectAdCredentials(companyId);
  const [accessToken, setAccessToken] = useState('');
  const [adAccountId, setAdAccountId] = useState('');
  const [pageIds, setPageIds] = useState('');
  // N6 — opt-in checkbox plumbed through pasteMetaCredentials Zod
  // (`enableMessengerDm: z.boolean().default(false)`) into
  // provisionMetaPageTokensAndSubscribe which CSV-appends `messages` to
  // the existing `leadgen` subscription. Off by default — tenants must
  // explicitly opt in to receive Page DMs, separately from the
  // featureFlags.meta_messenger_dm.mode runtime flag. Canonical plan §1.3.
  const [enableMessengerDm, setEnableMessengerDm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!accessToken.trim() || !adAccountId.trim()) {
      setError('Access token and ad account ID are required');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await pasteMeta({
        accessToken: accessToken.trim(),
        adAccountId: adAccountId.trim(),
        connectedPageIds: pageIds
          .split(/[\n,]/)
          .map((s) => s.trim())
          .filter(Boolean),
        enableMessengerDm,
      });
      setConnected(true);
      onConnected?.();
      setTimeout(() => {
        onClose();
        setConnected(false);
        setAccessToken('');
        setAdAccountId('');
        setPageIds('');
        setEnableMessengerDm(false);
      }, 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded bg-[#1877f2] text-white inline-flex items-center justify-center font-bold text-sm">
              f
            </span>
            <h2 className="text-base font-semibold text-white">
              Connect Meta (Facebook + Instagram) Ads
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-800 text-gray-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-300 flex gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              OAuth consent flow activates once Meta App Review is approved
              (3–10 business days). Until then, paste credentials from your
              Meta Business Settings &rarr; System Users.
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">
              System User access token
            </label>
            <input
              type="password"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              placeholder="EAA..."
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white font-mono text-xs"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">
              Ad account ID (act_xxxxxxxxxx)
            </label>
            <input
              type="text"
              value={adAccountId}
              onChange={(e) => setAdAccountId(e.target.value)}
              placeholder="act_1234567890"
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white font-mono text-xs"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">
              Page IDs (one per line — optional, used for Lead Ads webhooks)
            </label>
            <textarea
              value={pageIds}
              onChange={(e) => setPageIds(e.target.value)}
              placeholder={'102938475\n384756281'}
              rows={3}
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white font-mono text-xs resize-none"
            />
          </div>
          {/* Messenger DM opt-in. The subscription side-effect is
              triggered server-side (provisionMetaPageTokensAndSubscribe
              CSV-appends `messages` to `subscribed_fields`). The runtime
              processing of inbound DMs is independently gated by the
              per-tenant featureFlags.meta_messenger_dm.mode flag — this
              checkbox only registers Meta-side intent. */}
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={enableMessengerDm}
              onChange={(e) => setEnableMessengerDm(e.target.checked)}
              className="mt-0.5 accent-blue-500"
            />
            <span className="text-xs text-gray-300 leading-relaxed">
              <span className="font-semibold text-white">
                Also forward Page DMs to my Valutten inbox
              </span>
              <span className="block text-[11px] text-gray-500 mt-0.5">
                Subscribes the connected Pages to Messenger inbound webhooks.
                Replies are sent from the broker portal’s Conversations inbox,
                inside Meta’s standard 24-hour reply window.
              </span>
            </span>
          </label>
          {error && (
            <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-300">
              {error}
            </div>
          )}
          {connected && (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-300 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Meta credentials stored.
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm text-gray-300 hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || connected}
            className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {saving ? 'Connecting…' : 'Connect'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConnectMetaModal;
